import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAppContext } from '../App';

const API = 'https://api.cpms-srlkevcharge.com';

const IconEdit = () => (
  <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path d="M17 3a2.828 2.828 0 114 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
  </svg>
);
const IconTrash = () => (
  <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
  </svg>
);

function StatusPill({ status }) {
  const s = (status || 'draft').toLowerCase();
  return <span className={`tariff-status-pill tariff-status-${s}`}>{status}</span>;
}

export default function ViewTariffs() {
  const [tariffs, setTariffs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const navigate = useNavigate();
  const { showToast } = useAppContext();

  const fetchTariffs = async () => {
    try {
      const res = await axios.get(`${API}/tariffs/`);
      setTariffs(res.data);
    } catch (e) {
      console.error('Failed to fetch tariffs', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTariffs(); }, []);

  const stats = {
    total: tariffs.length,
    active: tariffs.filter(t => t.status === 'Active').length,
    inactive: tariffs.filter(t => t.status === 'Inactive').length,
    draft: tariffs.filter(t => t.status === 'Draft').length,
  };

  const filtered = tariffs.filter(t =>
    !filter ||
    t.name.toLowerCase().includes(filter.toLowerCase()) ||
    t.organisation_name?.toLowerCase().includes(filter.toLowerCase()) ||
    t.status.toLowerCase().includes(filter.toLowerCase())
  );

  const formatPrice = (tariff) => {
    if (!tariff.price_components || tariff.price_components.length === 0) return '—';
    const first = tariff.price_components[0];
    const suffix = tariff.price_components.length > 1 ? ` +${tariff.price_components.length - 1} more` : '';
    return `${tariff.currency} ${first.price_per_unit} / ${first.charging_unit}${suffix}`;
  };

  const handleDelete = async (tariff) => {
    if (tariff.location_count > 0) {
      showToast(`Cannot delete — ${tariff.location_count} station(s) are using this tariff`, 'error');
      return;
    }
    if (!window.confirm(`Delete the tariff "${tariff.name}"? This cannot be undone.`)) return;

    try {
      await axios.delete(`${API}/tariffs/${tariff.id}`);
      showToast(`Tariff "${tariff.name}" removed successfully`);
      fetchTariffs();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to delete tariff', 'error');
    }
  };

  if (loading) return <div className="loading">Loading tariffs...</div>;

  return (
    <>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16 }}>
        <div>
          <h1>Tariffs</h1>
          <p>All pricing structures across the network</p>
        </div>
        <button className="btn btn-green" onClick={() => navigate('/tariffs/create')}>
          + Create Tariff
        </button>
      </div>

      <div className="stat-bar">
        <div className="card stat-card">
          <div className="stat-label">Total Tariff</div>
          <div className="stat-number total">{stats.total}</div>
        </div>
        <div className="card stat-card">
          <div className="stat-label">Total Active Tariff</div>
          <div className="stat-number available">{stats.active}</div>
        </div>
        <div className="card stat-card">
          <div className="stat-label">Total Inactive Tariff</div>
          <div className="stat-number" style={{ color: 'var(--slate)' }}>{stats.inactive}</div>
        </div>
        <div className="card stat-card">
          <div className="stat-label">Total Draft Tariff</div>
          <div className="stat-number" style={{ color: 'var(--amber)' }}>{stats.draft}</div>
        </div>
      </div>

      <div className="card">
        <div className="section-header">
          <h2>Tariff List</h2>
        </div>
        <div style={{ padding: '0 20px 16px' }}>
          <input
            className="filter-input"
            placeholder="Search by name, organisation or status..."
            value={filter}
            onChange={e => setFilter(e.target.value)}
            style={{ width: 300 }}
          />
        </div>
        <div className="table-wrap">
          {filtered.length === 0 ? (
            <div className="empty-state">
              <h3>{tariffs.length === 0 ? 'No data found' : 'No results'}</h3>
              <p>
                {tariffs.length === 0
                  ? 'Create your first tariff to start pricing your stations.'
                  : 'Try a different search term.'}
              </p>
            </div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Tariff Name</th>
                  <th>Organisation</th>
                  <th>Pricing</th>
                  <th>Currency</th>
                  <th>Stations Using</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(t => (
                  <tr key={t.id}>
                    <td style={{ fontWeight: 600 }}>{t.name}</td>
                    <td style={{ color: 'var(--text-muted)' }}>{t.organisation_name}</td>
                    <td>{formatPrice(t)}</td>
                    <td>{t.currency}</td>
                    <td>{t.location_count}</td>
                    <td><StatusPill status={t.status} /></td>
                    <td>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button
                          className="btn btn-ghost"
                          style={{ padding: '5px 12px', fontSize: 12 }}
                          onClick={() => navigate(`/tariffs/${t.id}/edit`)}
                        >
                          <IconEdit /> Edit
                        </button>
                        <button
                          className="btn btn-danger"
                          style={{ padding: '5px 12px', fontSize: 12 }}
                          onClick={() => handleDelete(t)}
                        >
                          <IconTrash /> Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </>
  );
}